import * as Sentry from '@sentry/react';
import {useQuery} from '@tanstack/react-query';
import * as I from 'immutable';
import React from 'react';

import {useFetchAlertPoliciesForFeatureCollections} from 'app/components/Alerts/AlertPolicyProvider';
import {alertPolicyUsesOverlay} from 'app/components/Alerts/alertPolicyUtils';
import api from 'app/modules/Remote/api';
import {ApiFeatureCollection, GeometryOverlaySetting} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOverlay} from 'app/modules/Remote/Organization';
import {useProjects} from 'app/providers/ProjectsProvider';
import {recordEvent} from 'app/tools/Analytics';
import {
  findPrimaryFeatureCollection,
  getFeatureCollectionsByType,
  getLensView,
  getPrimaryFeatureCollectionIds,
  updateLensView,
} from 'app/utils/featureCollectionUtils';
import {useCachedApiGet} from 'app/utils/hookUtils';
import {findProjectInProjects} from 'app/utils/projectUtils';

import {LibraryDataset, LibraryDatasetType, OverlayDataset} from './LibraryTypes';
import {FEATURED_OVERLAY_METADATA} from './overlaysConfig';

export interface OverlaysActions {
  addOverlays: (projectIds: string[], featureCollectionId: number) => Promise<void>;
  removeOverlays: (projectIds: string[], featureCollectionId: number) => Promise<void>;
  deleteOverlay: (featureCollectionId: number) => Promise<void>;
  downloadOverlay: (featureCollectionId: number) => Promise<void>;
  updateOverlay: (featureCollectionId: number, name: string) => Promise<void>;
}

interface OverlaysContextValue {
  actions: OverlaysActions;
  //TODO(eva): make overlays not return immutable to make life easier in the two spots downstream
  overlays: I.ImmutableListOf<ApiOverlay>;
  upstreamOverlays: LibraryDataset<OverlayDataset>[];
  meta: {loading: boolean; error: Error | string | null};
}

export const OverlaysContext = React.createContext<OverlaysContextValue | undefined>(undefined);

export const OverlaysProvider: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.ImmutableOf<ApiOrganization>;
  }>
> = ({organization, children}) => {
  const organizationId = organization.get('id');

  const [projectsMap, featureCollectionsById, projectsActions] = useProjects();
  const fetchAlertPolicies = useFetchAlertPoliciesForFeatureCollections();

  const [getOverlays, {invalidate, updateLocal}] = useCachedApiGet(async (_, orgId: string) => {
    const overlays = (await api.organizations.getOverlays(orgId)).get('data');
    return overlays;
  }, []);

  const overlaysResult = getOverlays(organizationId);
  const overlays = overlaysResult.value?.toList() || I.List([]);
  const overlaysLoading = overlaysResult.status === 'unknown';
  const overlaysError = overlaysResult.error ?? null;

  const addOverlays = async (projectIds: string[], overlayFeatureCollectionId: number) => {
    try {
      recordEvent('Added dataset', {featureCollectionId: overlayFeatureCollectionId});
      // Optimistically update local data to show the new associations between projId and overlay
      updateLocal([organizationId], (prevOverlays) =>
        prevOverlays
          ?.map((overlay) =>
            overlay!.get('featureCollectionId') === overlayFeatureCollectionId
              ? overlay!.update('projectIds', (prevProjectIds) =>
                  prevProjectIds.concat(I.fromJS(projectIds)).toList()
                )
              : overlay!
          )
          .toList()
      );

      await Promise.all(
        projectIds.map((projId) => api.projects.addOverlay(projId, overlayFeatureCollectionId))
      );

      // Update the projects to include the new overlay without reloading the page
      if (projectsMap) {
        await Promise.all(
          projectIds.map((projId) => {
            const projectOverlays = projectsMap.toJS()[projId].featureCollections;
            projectOverlays.push({id: overlayFeatureCollectionId, kind: 'overlay'});
            projectsActions.updateProject(projId, {featureCollections: projectOverlays});
          })
        );
      }
    } catch (e) {
      // e is unknown, add a type, use optional chaining, and provide a default
      const errorBody = (e as {body: {error: string}; error: Error})?.body?.error || '';
      //if error, roll back local data to the server data
      /** NOTE: You could pass `true` as a second argument to the invalidate
       * function to clear out the local cache while the request is happening.
       * We’d probably only want to do this if we have a loading state. */
      invalidate([organizationId]);
      Sentry.captureException(e);
      // In lieu of actual error types or enums, check to see if this error is about being readonly
      const errorMessage = errorBody.includes('readonly')
        ? 'Please reach out to an admin or member on your team to add overlays.'
        : 'We weren’t able to add these overlays. Please try again.';
      window.alert(errorMessage);
    }
  };

  /**
   * How many alert policies on the affected projects reference this overlay, or
   * null when that can't be determined — the caller must not read null as zero.
   *
   * Only primary feature collections own policies, so the request keys on every
   * primary id, matching the library's prefetch and reading from its cache; the
   * affected subset is filtered out of the result in memory.
   */
  const countAlertPoliciesForOverlay = async (
    projectIds: string[],
    overlayFeatureCollectionId: number
  ): Promise<number | null> => {
    if (!projectsMap) {
      return null;
    }

    try {
      const affectedPrimaryIds = new Set<number>();
      for (const projId of projectIds) {
        const project = findProjectInProjects(projectsMap, projId);
        // A project id that no longer resolves means its policies can't be checked.
        if (!project) {
          return null;
        }
        // A project with no live primary — an archived one, say — owns no policies.
        const fcId = findPrimaryFeatureCollection(project, featureCollectionsById)?.get('id');
        if (fcId !== undefined) {
          affectedPrimaryIds.add(fcId);
        }
      }

      const alertPolicies = await fetchAlertPolicies(
        getPrimaryFeatureCollectionIds(projectsMap, featureCollectionsById)
      );
      return alertPolicies.filter(
        (ap) =>
          affectedPrimaryIds.has(Number(ap.featureCollectionId)) &&
          alertPolicyUsesOverlay(ap, overlayFeatureCollectionId)
      ).length;
    } catch (e) {
      Sentry.captureException(e);
      return null;
    }
  };

  const removeOverlays = async (projectIds: string[], overlayFeatureCollectionId: number) => {
    const affectedAlertPolicyCount = await countAlertPoliciesForOverlay(
      projectIds,
      overlayFeatureCollectionId
    );
    const confirmMessage =
      affectedAlertPolicyCount === null
        ? 'We weren’t able to check whether any alert policies use this overlay. Are you sure you want to remove these overlays?'
        : affectedAlertPolicyCount > 0
          ? `Are you sure you want to remove these overlays? This will affect ${affectedAlertPolicyCount} alert policies that use this overlay.`
          : null;
    if (confirmMessage && !confirm(confirmMessage)) {
      return;
    }
    try {
      // Optimistically update local data to remove associations between projId and overlay
      updateLocal([organizationId], (prevOverlays) =>
        prevOverlays
          ?.map((overlay) =>
            overlay!.get('featureCollectionId') === overlayFeatureCollectionId
              ? overlay!.update('projectIds', (prevProjectIds) =>
                  prevProjectIds
                    .filter((prevProjId) => !I.fromJS(projectIds).includes(prevProjId!))
                    .toList()
                )
              : overlay!
          )
          .toList()
      );
      await Promise.all(
        projectIds.map((projId) => api.projects.removeOverlay(projId, overlayFeatureCollectionId))
      );

      // Update the projects to remove the new overlay without reloading the page
      if (projectsMap) {
        await Promise.all(
          projectIds.map((projId) => {
            const projectOverlays = projectsMap.toJS()[projId].featureCollections;
            projectOverlays.pop({id: overlayFeatureCollectionId, kind: 'overlay'});
            projectsActions.updateProject(projId, {featureCollections: projectOverlays});
          })
        );
      }
    } catch (e) {
      // e is unknown, add a type, use optional chaining, and provide a default
      const errorBody = (e as {body: {error: string}; error: Error})?.body?.error || '';
      //if error, roll back local data to the server data
      invalidate([organizationId]);
      Sentry.captureException(e);
      // In lieu of actual error types or enums, check to see if this error is about being readonly
      const errorMessage = errorBody.includes('readonly')
        ? 'Please reach out to an admin or member on your team to remove overlays.'
        : 'We weren’t able to remove these overlays. Please try again.';
      window.alert(errorMessage);
    }
  };

  // this is a soft delete-- this will set is_archived = True for this feature collection; and the GET
  // overlays endpoint will filter out any overlays where is_archived == True.
  const deleteOverlay = async (featureCollectionId: number) => {
    try {
      // Optimistically update local data to remove our deleted overlay from the list of overlays
      updateLocal([organizationId], (prevOverlays) =>
        prevOverlays!.filter((o) => o!.get('featureCollectionId') !== featureCollectionId).toList()
      );
      await api.featureCollections.archiveFeatureCollection(featureCollectionId);
    } catch (e) {
      // e is unknown, add a type, use optional chaining, and provide a default
      const errorBody = (e as {body: {error: string}; error: Error})?.body?.error || '';
      //if error, roll back local data to the server data
      invalidate([organizationId]);
      Sentry.captureException(e);
      // In lieu of actual error types or enums, check to see if this error is about being readonly
      const errorMessage = errorBody.includes('readonly')
        ? 'Please reach out to an admin or member on your team to delete this overlay.'
        : 'We weren’t able to delete this overlay from your library. Please try again.';
      window.alert(errorMessage);
    }
  };

  const downloadOverlay = async (featureCollectionId: number) => {
    await api.featureCollections.export(featureCollectionId);
  };

  const updateOverlay = async (featureCollectionId: number, name: string) => {
    try {
      updateLocal([organizationId], (prevOverlays) =>
        prevOverlays
          ?.map((overlay) =>
            overlay!.get('featureCollectionId') === featureCollectionId
              ? overlay!.set('name', name)
              : overlay!
          )
          .toList()
      );
      await api.featureCollections.update(featureCollectionId, {name});
    } catch (e) {
      const errorBody = (e as {body: {error: string}; error: Error})?.body?.error || '';
      invalidate([organizationId]);
      Sentry.captureException(e);
      const errorMessage = errorBody.includes('readonly')
        ? 'Please reach out to an admin or member on your team to rename this overlay.'
        : 'We weren’t able to rename this overlay. Please try again.';
      window.alert(errorMessage);
    }
  };

  const actions: OverlaysActions = {
    addOverlays,
    removeOverlays,
    deleteOverlay,
    downloadOverlay,
    updateOverlay,
  };

  const upstreamOverlays: LibraryDataset<OverlayDataset>[] = React.useMemo(() => {
    const filteredOverlays = overlays
      .filter((o) => o?.get('uploadedBy') === 'Upstream System')
      .toJS();

    const libraryOverlays = filteredOverlays.map((overlay) => {
      if (!FEATURED_OVERLAY_METADATA[overlay.name]) {
        console.warn(
          `No metadata found for overlay ${overlay.name}. This overlay will be skipped.`
        );
        return null;
      }
      return {
        ...overlay,
        source: FEATURED_OVERLAY_METADATA[overlay.name].source,
        geographicExtent: FEATURED_OVERLAY_METADATA[overlay.name].geographicExtent,
        description: FEATURED_OVERLAY_METADATA[overlay.name].description,
        restriction: FEATURED_OVERLAY_METADATA[overlay.name].restriction,
        tags: FEATURED_OVERLAY_METADATA[overlay.name].tags,
        license: FEATURED_OVERLAY_METADATA[overlay.name].license,
        libraryKey: FEATURED_OVERLAY_METADATA[overlay.name].libraryKey,
        mapHelperText: FEATURED_OVERLAY_METADATA[overlay.name].mapHelperText,
        type: LibraryDatasetType.OVERLAY,
      };
    });
    return libraryOverlays.filter((o) => !!o);
  }, [overlays]);

  const value = React.useMemo(
    (): OverlaysContextValue => ({
      actions: actions,
      overlays: overlays,
      upstreamOverlays: upstreamOverlays,
      meta: {loading: overlaysLoading, error: overlaysError},
    }),
    [actions, overlays, upstreamOverlays, overlaysLoading, overlaysError]
  );

  return <OverlaysContext.Provider value={value}>{children}</OverlaysContext.Provider>;
};

export function useOverlays(): OverlaysContextValue {
  const value = React.useContext(OverlaysContext);

  if (!value) {
    throw new Error('useOverlays must be beneath an OverlaysProvider');
  }

  return value;
}

export const FakeOverlaysProvider: React.FunctionComponent<
  React.PropsWithChildren<{
    actions: OverlaysActions;
    overlays: I.ImmutableListOf<ApiOverlay>;
    upstreamOverlays: LibraryDataset<OverlayDataset>[];
    meta?: {loading: boolean; error: any};
  }>
> = ({children, actions, overlays, upstreamOverlays, meta = {loading: false, error: null}}) => {
  const value = React.useMemo(
    (): OverlaysContextValue => ({
      actions: actions,
      overlays: overlays,
      upstreamOverlays: upstreamOverlays,
      meta,
    }),
    [actions, overlays, upstreamOverlays, meta]
  );

  return <OverlaysContext.Provider value={value}>{children}</OverlaysContext.Provider>;
};

// Every overlay in the organization's library, on a portfolio or not. The lookout modal isn't
// under an OverlaysProvider, so it fetches the library on its own.
export const useOrganizationOverlays = (organizationId: string) =>
  useQuery({
    queryKey: ['organizationOverlays', organizationId],
    queryFn: async (): Promise<ApiOverlay[]> =>
      (await api.organizations.getOverlays(organizationId)).get('data').toJS() as ApiOverlay[],
    staleTime: 1000 * 60 * 5,
  });

export const useProjectOverlays = (featureCollection: I.ImmutableOf<ApiFeatureCollection>) => {
  const [projects, featureCollectionsById, projectsActions] = useProjects();

  const projectForFeatureCollection = React.useMemo(() => {
    if (!projects) return null;
    return projects.find((proj) =>
      proj!.get('featureCollections').some((pfc) => pfc!.get('id') === featureCollection.get('id'))
    );
  }, [projects, featureCollection]);

  // This has all the available overlays with their IDs.
  const projectOverlays: ApiFeatureCollection[] = React.useMemo(() => {
    if (!projectForFeatureCollection || !featureCollectionsById) return I.List([]);
    const [, overlays] = getFeatureCollectionsByType(
      projectForFeatureCollection,
      featureCollectionsById
    );
    return overlays ? overlays.toJS() : [];
  }, [projectForFeatureCollection, featureCollectionsById]);

  // Derived from the lens view, this has the settings/state for overlays.
  const overlaySettings = React.useMemo(() => {
    const lensView = getLensView(featureCollection);
    const lensOverlays = lensView?.get('overlays');
    const map = new Map<string, GeometryOverlaySetting>();
    lensOverlays?.forEach((o) => {
      if (!o) return;
      map.set(o.get('name'), o.toJS() as GeometryOverlaySetting);
    });
    return map;
  }, [featureCollection]);

  const setOverlaySettings = React.useCallback(
    (overlaySettings: GeometryOverlaySetting[]) => {
      projectsActions.updateFeatureCollection(
        featureCollection.get('id'),
        updateLensView(featureCollection, (v) => v.set('overlays', I.fromJS(overlaySettings)))
      );
    },
    [featureCollection, projectsActions]
  );

  return {projectOverlays, overlaySettings, setOverlaySettings};
};

export default OverlaysProvider;
